import { makeStyles } from '@material-ui/styles'
import IconButton from 'core/elements/button/IconButton'
import Dropdown from 'core/elements/dropdown'
import Input from 'core/elements/input'
import useParams from 'core/hooks/useParams'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { VMServerSideFilters } from './VMServerSideFilters'
import Button from 'core/elements/button'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'grid',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  firstCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  secondCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  toggle: {
    marginBottom: theme.spacing(1),
  },
  searchSection: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  searchDropdown: {
    minWidth: 150,
    '& ul': {
      left: '-1px',
    },
  },
  searchInput: {
    minWidth: 200,
    flex: 1,
    gap: 0,
  },
  iconButton: {
    width: 38,
    height: 38,
  },
  clearButton: {
    height: 38,
  },
  dropdown: {
    '& .inputFrame': {
      borderWidth: 1,
    },
  },
  secondRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    justifyContent: 'flex-start',
  },
}))

export interface SearchFieldOption {
  value: string
  label: string
}

interface VMServerSideSearchProps {
  onSearch: (field: string, value: string, filterParams?: Record<string, string>) => void
  onClear: () => void
  isSearching?: boolean
  isLoading?: boolean
  sortComponent?: React.ReactNode
  togglesComponent?: React.ReactNode
  initialSearchParams?: Record<string, string> | null
  initialFilterParams?: Partial<VMFilterParams>
  allTenants?: boolean
  excludeFilters?: string[]
}

const SEARCH_FIELD_OPTIONS: SearchFieldOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'uuid', label: 'UUID' },
  { value: 'ip', label: 'IPv4 Address' },
  { value: 'ip6', label: 'IPv6 Address' },
  { value: 'key_name', label: 'Key Name' },
]

export type VMFilterParams = {
  availability_zone: string
  vm_state: string
  user_id: string
  flavor: string
  image: string
  host: string
  project_id?: string
}

export const defaultVMFilterParams: VMFilterParams = {
  availability_zone: 'all',
  vm_state: 'all',
  user_id: 'all',
  flavor: 'all',
  image: 'all',
  host: 'all',
  project_id: undefined,
}

export const VMServerSideSearch: React.FC<VMServerSideSearchProps> = ({
  onSearch,
  onClear,
  isSearching = false,
  isLoading = false,
  sortComponent,
  togglesComponent,
  initialSearchParams,
  initialFilterParams,
  allTenants = false,
  excludeFilters = [],
}) => {
  const classes = useStyles()
  const SEARCH_FIELD_KEYS = useMemo(() => SEARCH_FIELD_OPTIONS.map((option) => option.value), [])

  const getInitialField = () => {
    if (initialSearchParams) {
      // Find first key that's a search field (not a filter)
      const field = Object.keys(initialSearchParams).find((key) => SEARCH_FIELD_KEYS.includes(key))
      return field || 'name'
    }
    return 'name'
  }

  const getInitialValue = () => {
    if (initialSearchParams) {
      // Find first key that's a search field (not a filter)
      const field = Object.keys(initialSearchParams).find((key) => SEARCH_FIELD_KEYS.includes(key))
      return field ? initialSearchParams[field] || '' : ''
    }
    return ''
  }

  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [searchField, setSearchField] = useState<string>(getInitialField())
  const [searchValue, setSearchValue] = useState<string>(getInitialValue())
  const [userHasTyped, setUserHasTyped] = useState(false)

  // Filter params using useParams hook
  const { params: filterParams, updateParams } = useParams<VMFilterParams>({
    availability_zone:
      initialFilterParams?.availability_zone || defaultVMFilterParams.availability_zone,
    vm_state: initialFilterParams?.vm_state || defaultVMFilterParams.vm_state,
    user_id: initialFilterParams?.user_id || defaultVMFilterParams.user_id,
    flavor: initialFilterParams?.flavor || defaultVMFilterParams.flavor,
    image: initialFilterParams?.image || defaultVMFilterParams.image,
    host: initialFilterParams?.host || defaultVMFilterParams.host,
    project_id: initialFilterParams?.project_id,
  })

  // Track if we're making an internal change (to prevent circular updates)
  const isInternalChangeRef = useRef(false)

  // Sync filter params when initialFilterParams changes (e.g., when allTenants toggles)
  // But skip if this is a circular update from our own search callback
  const initialFilterParamsString = JSON.stringify(initialFilterParams)
  const prevFilterParamsRef = useRef(filterParams)

  useEffect(() => {
    // Skip if this update is from our own search (circular update)
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false
      return
    }

    // Only sync if the values are actually different from current local state
    if (initialFilterParams) {
      const shouldSync =
        filterParams.availability_zone !== (initialFilterParams.availability_zone || 'all') ||
        filterParams.vm_state !== (initialFilterParams.vm_state || 'all') ||
        filterParams.user_id !== (initialFilterParams.user_id || 'all') ||
        filterParams.flavor !== (initialFilterParams.flavor || 'all') ||
        filterParams.image !== (initialFilterParams.image || 'all') ||
        filterParams.host !== (initialFilterParams.host || 'all') ||
        filterParams.project_id !== initialFilterParams.project_id

      if (shouldSync) {
        updateParams({
          availability_zone:
            initialFilterParams.availability_zone || defaultVMFilterParams.availability_zone,
          vm_state: initialFilterParams.vm_state || defaultVMFilterParams.vm_state,
          user_id: initialFilterParams.user_id || defaultVMFilterParams.user_id,
          flavor: initialFilterParams.flavor || defaultVMFilterParams.flavor,
          image: initialFilterParams.image || defaultVMFilterParams.image,
          host: initialFilterParams.host || defaultVMFilterParams.host,
          project_id: initialFilterParams.project_id,
        })
      }
    }
    prevFilterParamsRef.current = filterParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilterParamsString])

  // Only sync when search is explicitly cleared (initialSearchParams becomes null)
  // Don't sync when it changes to a value, as the user controls the input
  const searchParamsString = JSON.stringify(initialSearchParams)

  useEffect(() => {
    // Only clear when explicitly cleared from parent
    if (!initialSearchParams && searchValue) {
      setSearchField('name')
      setSearchValue('')
      setUserHasTyped(false)
    }
    // Sync search field and value when params change (including allTenants toggle)
    else if (initialSearchParams) {
      // Find first key that's a search field (not a filter)
      const field = Object.keys(initialSearchParams).find((key) => SEARCH_FIELD_KEYS.includes(key))
      const value = field ? initialSearchParams[field] || '' : ''

      // Update if different from current values
      if (field && field !== searchField) {
        setSearchField(field)
      }
      if (value !== searchValue) {
        setSearchValue(value)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString])

  // Helper to build filter params
  const buildFilterParams = useCallback(() => {
    const apiParams: Record<string, string> = {}

    // Note: Don't add all_tenants here - it's handled by the action (allTenants parameter)
    // Only add project_id when filtering by specific tenant

    if (filterParams.availability_zone && filterParams.availability_zone !== 'all') {
      apiParams.availability_zone = filterParams.availability_zone
    }
    if (filterParams.vm_state && filterParams.vm_state !== 'all') {
      apiParams.vm_state = filterParams.vm_state
    }
    if (filterParams.user_id && filterParams.user_id !== 'all') {
      apiParams.user_id = filterParams.user_id
    }
    if (filterParams.flavor && filterParams.flavor !== 'all') {
      apiParams.flavor = filterParams.flavor
    }
    if (filterParams.image && filterParams.image !== 'all') {
      apiParams.image = filterParams.image
    }
    if (filterParams.host && filterParams.host !== 'all') {
      apiParams.host = filterParams.host
    }
    // Include project_id when allTenants is true and a specific tenant is selected
    if (allTenants && filterParams.project_id && filterParams.project_id !== 'all') {
      apiParams.project_id = filterParams.project_id
    }
    return apiParams
  }, [filterParams, allTenants])

  const handleSearch = useCallback(() => {
    isInternalChangeRef.current = true
    const apiParams = buildFilterParams()
    onSearch(searchField, searchValue.trim(), apiParams)
  }, [searchField, searchValue, buildFilterParams, onSearch])

  // // Auto-trigger search when filters change
  // const filterChangeRef = useRef(false)
  // const skipAutoTriggerRef = useRef(false)
  // useEffect(() => {
  //   // Skip on initial mount
  //   if (!filterChangeRef.current) {
  //     filterChangeRef.current = true
  //     return
  //   }
  //   // Skip if explicitly told to (e.g., during clear filters)
  //   if (skipAutoTriggerRef.current) {
  //     skipAutoTriggerRef.current = false
  //     return
  //   }
  //   isInternalChangeRef.current = true
  //   const apiParams = buildFilterParams()
  //   onSearch(searchField, searchValue.trim(), apiParams)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [filterParams])

  // Reset search input and triggers a reload (keeps filters)
  const handleClear = useCallback(() => {
    setSearchField('name')
    setSearchValue('')
    setUserHasTyped(false)
    // Clear only the search, keep current filters
    const apiParams = buildFilterParams()
    onSearch(searchField, '', apiParams)
  }, [onSearch, buildFilterParams, searchField])

  // Detect when user manually clears the search input
  const prevSearchValue = useRef(searchValue)
  useEffect(() => {
    // // If search value becomes empty and user had typed before, treat as clear
    // if (prevSearchValue.current && !searchValue && userHasTyped) {
    //   // User manually cleared the search input
    //   handleClear()
    // }
    prevSearchValue.current = searchValue
  }, [searchValue, userHasTyped, handleClear])

  // Handle clear filters AND search (Clear All button)
  const handleClearFilters = useCallback(() => {
    // Prevent auto-trigger from firing with old values
    // skipAutoTriggerRef.current = true
    // Reset search UI state
    setSearchField('name')
    setSearchValue('')
    setUserHasTyped(false)
    // Clear filters to defaults
    updateParams(defaultVMFilterParams)
    // Call parent's clear handler to properly reset everything
    onClear()
  }, [updateParams, onClear])

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && searchValue?.trim() !== '') {
        handleSearch()
      }
    },
    [handleSearch],
  )

  const isDisabled = isLoading || isSearching

  // Check if any filter is active (not 'all')
  const hasActiveFilters = useMemo(() => {
    return (
      (!excludeFilters.includes('availability_zone') && filterParams.availability_zone !== 'all') ||
      (!excludeFilters.includes('vm_state') && filterParams.vm_state !== 'all') ||
      (!excludeFilters.includes('user_id') && filterParams.user_id !== 'all') ||
      (!excludeFilters.includes('flavor') && filterParams.flavor !== 'all') ||
      (!excludeFilters.includes('image') && filterParams.image !== 'all') ||
      (!excludeFilters.includes('host') && filterParams.host !== 'all') ||
      (allTenants && filterParams.project_id && filterParams.project_id !== 'all') ||
      searchValue.trim() !== ''
    )
  }, [filterParams, searchValue, allTenants])

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue((prev) => {
        // When user manually clears the search input
        // Trigger a search with empty value to clear the results
        if (prev !== e.target.value && prev.trim() !== '' && e.target.value.trim() === '') {
          const apiParams = buildFilterParams()
          onSearch(searchField, e.target.value.trim(), apiParams)
        }

        return e.target.value
      })
      setUserHasTyped(true)
    },
    [buildFilterParams, onSearch, searchField],
  )

  return (
    <div className={classes.root}>
      <div className={classes.firstCol}>
        {/* First Section: Search  */}
        <div className={classes.searchSection}>
          <Dropdown
            label="Search By"
            className={classes.searchDropdown}
            items={SEARCH_FIELD_OPTIONS}
            value={searchField}
            onChange={(value) => setSearchField(value as string)}
            disabled={isDisabled}
          />
          <Input
            className={classes.searchInput}
            placeholder={`Search by ${SEARCH_FIELD_OPTIONS.find(
              (opt) => opt.value === searchField,
            )?.label.toLowerCase()}...`}
            value={searchValue}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyPress}
            disabled={isDisabled}
          />
          <IconButton
            className={classes.iconButton}
            onClick={handleSearch}
            disabled={isDisabled || !searchValue.trim()}
            size="md"
            info="Search"
            icon="search"
          />
          {/* Clear filters button */}
          {hasActiveFilters ? (
            <Button
              variant="secondary"
              className={classes.clearButton}
              onClick={handleClearFilters}
              disabled={isDisabled || !hasActiveFilters}
              rightIcon="times"
              info="Clear search and filters"
            >
              Clear All
            </Button>
          ) : (
            <IconButton
              className={classes.iconButton}
              onClick={() => {
                setShowFilters(!showFilters)
              }}
              disabled={isDisabled}
              info={showFilters ? 'Hide Filters' : 'Show Additional Filters'}
              icon="filter"
            />
          )}
        </div>
        <div className={classes.secondCol}>
          <div className={classes.toggle}>{togglesComponent}</div>
          {sortComponent}
        </div>
      </div>
      {/* Third Section: Sort */}
      {(showFilters || hasActiveFilters) && (
        <div className={classes.secondRow}>
          <VMServerSideFilters
            params={filterParams}
            updateParams={updateParams}
            disabled={isDisabled}
            allTenants={allTenants}
            excludeFilters={excludeFilters}
          />
          <Button onClick={handleSearch} disabled={isDisabled} info="Apply Filters">
            Apply
          </Button>
        </div>
      )}
    </div>
  )
}
